import { useState } from "react";
import i18next from "i18next";
import { Trans, useTranslation } from "react-i18next";
import type { IWorkspaceItem } from "#src/services/workspace";
import { IWorkspacePaymentPlanItem } from "#src/services/payment/types";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { ModalHeaderIcon } from "#src/commonV2/modal/ModalHeaderIcon";
import { XClose } from "@untitled-ui/icons-react";
import constants from "#src/config/constants";
import { InputV2 } from "#src/commonV2/inputs/Input";
import { ButtonV2 } from "#src/commonV2/Button";
import paymentService from "#src/services/payment";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

interface IBillingServiceUnsubModalProps {
  unsubbingService: IWorkspacePaymentPlanItem | null;
  onClose: () => void;
  currentWorkspace: IWorkspaceItem | undefined;
}

export const BillingServiceUnsubModal = ({
  unsubbingService,
  onClose,
  currentWorkspace,
}: IBillingServiceUnsubModalProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "billing.tab.solution.unsubModal",
  });

  const locale = i18next.language;

  const [passcode, setPasscode] = useState<string>("");

  // --------------- METHODS ---------------

  const onCloseAndCleanup = () => {
    setPasscode("");
    onClose();
  };

  const onClickUnsubscribe = () => {
    if (unsubbingService && passcode === currentWorkspace?.name) {
      // TODO: unsub
      paymentService
        .cancelProductPlan(currentWorkspace.id, unsubbingService.id)
        .then((response) => {
          onCloseAndCleanup();
          console.log(response);
        })
        .catch(apiErrorHandler);
    }
  };

  // --------------- RENDER ---------------

  return (
    <ModalV2
      open={unsubbingService !== null}
      onChangeOpen={(value) => {
        if (!value) {
          onCloseAndCleanup();
        }
      }}
      className="gap-8"
    >
      <div className="flex gap-4">
        <ModalHeaderIcon type="error" Icon={XClose} />
        <div className="flex flex-col gap-4">
          <span className="text-lg font-semibold text-gray-v2-900">
            {t("unsubscibe")}
            {locale === "vi"
              ? unsubbingService?.product.name.vi
              : unsubbingService?.product.name.en}
          </span>
          <div className="flex flex-col gap-4 text-md text-gray-v2-600">
            {t("areYouSure")}
            <span className="contents font-semibold text-gray-v2-900">
              {locale === "vi"
                ? unsubbingService?.product.name.vi
                : unsubbingService?.product.name.en}
            </span>
            <br />
            <br />
            {unsubbingService?.product.id === "data_leak"
              ? t("afterUnsubbingDataLeak")
              : t("afterUnsubbing")}
            <br />
            <br />
            {t("contactToRefund")}
            <a
              href={`mailto:${constants.CYSTACK_SUPPORT}`}
              rel="noreferrer"
              target="_blank"
              className="contents font-semibold text-brand-v2-600 hover:text-brand-v2-700"
            >
              {constants.CYSTACK_SUPPORT}
            </a>
            {". "}
            <Trans
              t={t}
              i18nKey={"readMore"}
              components={{
                a: (
                  <a className="contents font-semibold text-brand-v2-600 hover:text-brand-v2-700" />
                ),
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-gray-v2-700">
              {t("toConfirm")}
              <span className="contents font-semibold text-gray-v2-900">
                {currentWorkspace?.name}
              </span>
            </span>
            <InputV2
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-1">
        <ButtonV2 variant="secondary" color="gray" onClick={onClose}>
          {t("button.cancel")}
        </ButtonV2>
        <ButtonV2
          color="error"
          disabled={passcode !== currentWorkspace?.name}
          onClick={onClickUnsubscribe}
        >
          {t("button.unsubscribe")}
        </ButtonV2>
      </div>
    </ModalV2>
  );
};
